import { useEffect, useState, ChangeEvent, useCallback, KeyboardEvent } from 'react';
import { Input, Text, Colors } from '@mercator-gcp/grout';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { useFormState } from 'hooks/projectGroupEdit/useProjectGroupForm';
import { useDashboardGroupName } from 'state/dashboard';

const editStyle = {
  justifyContent: 'flex-start',
  maxWidth: '600px',
  width: '100%',
};

const content = {
  saveHint: 'Press enter to save',
  errorMessage: 'Please enter a group name',
};

type GroupNameInputProps = {
  onEnter?: () => void;
};

const GroupNameInput = ({ onEnter }: GroupNameInputProps) => {
  const [groupName, setGroupName] = useDashboardGroupName();
  const [stateGroupName, setStateGroupName] = useState<string | undefined>('');
  const [isInputFocused, setIsInputFocused] = useState(false);
  const { isGroupNameInvalid, setIsGroupNameInvalid } = useFormState();
  const [enterPressed, setEnterPressed] = useState(false);

  useEffect(() => {
    setStateGroupName(groupName);
  }, [setStateGroupName, groupName]);

  const groupNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    /* We keep the typed value in local state and only push it into the Jotai
     * group atom on blur/enter. The group atom is persisted to sessionStorage,
     * so committing on every keystroke serializes the whole group and re-renders
     * every subscriber, which makes the input lag.
     */
    setStateGroupName(e.target.value);
  };

  const commitGroupNameToState = useCallback(() => {
    setGroupName(stateGroupName);
    if (stateGroupName) {
      setIsGroupNameInvalid(false);
    } else {
      setIsGroupNameInvalid(true);
    }
  }, [stateGroupName, setIsGroupNameInvalid, setGroupName]);

  const handleEnterKey = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        commitGroupNameToState();
        setEnterPressed(true);
      }
    },
    [commitGroupNameToState],
  );

  useEffect(() => {
    if (enterPressed) {
      onEnter?.();
      setEnterPressed(false);
    }
  }, [enterPressed, onEnter]);

  const handleInputOnBlur = useCallback(() => {
    commitGroupNameToState();
    setTimeout(() => setIsInputFocused(false), 0);
  }, [commitGroupNameToState, setIsInputFocused]);

  const handleInputFocus = useCallback(() => {
    setIsInputFocused(true);
  }, [setIsInputFocused]);

  return (
    <FlexColumn sx={editStyle}>
      <Input
        placeholder="Name your search"
        value={stateGroupName}
        onChange={groupNameChange}
        isInvalid={isGroupNameInvalid}
        error={isGroupNameInvalid ? content.errorMessage : ''}
        onBlur={handleInputOnBlur}
        onFocus={handleInputFocus}
        onKeyDown={handleEnterKey}
        autoFocus
      />
      {isInputFocused && (
        <Text.Small color={Colors.TextQuiet}>{content.saveHint}</Text.Small>
      )}
    </FlexColumn>
  );
};

export default GroupNameInput;
